import {useCallback, useLayoutEffect, useRef, useState} from 'react';
import {NfcSession, openNfcSession, toNfcError} from './webNfc';

export interface NfcOperationState {
    phase: 'idle' | 'waiting' | 'success' | 'error';
    message?: string;
}

interface OperationOptions<Result> {
    disabled?: boolean;
    identity?: string;
    onError?: (error: Error) => void;
    onSuccess: (result: Result) => void;
}

export const useNfcOperation = <Result,>(options: OperationOptions<Result>) => {
    const [state, setState] = useState<NfcOperationState>({phase: 'idle'});
    const sessionRef = useRef<NfcSession | null>(null);
    const optionsRef = useRef(options);

    useLayoutEffect(() => {
        optionsRef.current = options;
    });

    const stop = useCallback(() => {
        const session = sessionRef.current;
        sessionRef.current = null;
        session?.close();
    }, []);

    const cancel = useCallback(() => {
        stop();
        setState({phase: 'idle'});
    }, [stop]);

    useLayoutEffect(() => {
        cancel();
    }, [options.disabled, options.identity, cancel]);

    useLayoutEffect(() => stop, [stop]);

    const fail = (cause: unknown) => {
        const error = toNfcError(cause);
        setState({phase: 'error', message: error.message});
        optionsRef.current.onError?.(error);
    };

    const run = async (session: NfcSession, task: (session: NfcSession) => Promise<Result>) => {
        let result: Result;
        try {
            result = await task(session);
        } catch (cause) {
            if (sessionRef.current !== session) return;
            stop();
            fail(cause);
            return;
        }
        if (sessionRef.current !== session) return;
        stop();
        setState({phase: 'success'});
        optionsRef.current.onSuccess(result);
    };

    const start = (task: (session: NfcSession) => Promise<Result>) => {
        if (optionsRef.current.disabled || sessionRef.current) return;
        try {
            const session = openNfcSession();
            sessionRef.current = session;
            setState({phase: 'waiting'});
            void run(session, task);
        } catch (cause) {
            fail(cause);
        }
    };

    return {state, start, cancel};
};
